
import React, { useState, useMemo } from 'react';
import type { Recipe, Language } from '../types';
import XIcon from './icons/XIcon';
import ClipboardIcon from './icons/ClipboardIcon';
import CopiedNoticeModal from './CopiedNoticeModal';
import { getTranslation } from '../utils/translations';
import { formatRecipeTextFromLanguage } from '../utils/formatRecipeText';

interface RecipeExportModalProps {
    isOpen: boolean;
    onClose: () => void;
    recipe: Recipe | null;
    language?: Language;
}

const RecipeExportModal: React.FC<RecipeExportModalProps> = ({ isOpen, onClose, recipe, language }) => {
    const t = (key: any) => getTranslation(language, key);
    const [copied, setCopied] = useState(false);
    const [showCopiedNotice, setShowCopiedNotice] = useState(false);

    const formattedText = useMemo(() => {
        if (!recipe) return '';
        return formatRecipeTextFromLanguage(recipe, language);
    }, [recipe, language]);

    if (!isOpen || !recipe) return null;

    const handleCopy = () => {
        if (!formattedText) return;
        navigator.clipboard.writeText(formattedText).then(() => {
            setCopied(true);
            setShowCopiedNotice(true);
            setTimeout(() => setCopied(false), 2000);
        }).catch(err => console.error("Failed to copy", err));
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[300] flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
            <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden border border-white/20 dark:border-slate-800 transition-colors" onClick={e => e.stopPropagation()}>
                
                {/* Header */}
                <div className="p-6 bg-slate-50/50 dark:bg-slate-800/50 border-b dark:border-slate-800 flex justify-between items-center">
                    <h3 className="font-black text-slate-800 dark:text-slate-100 tracking-tight text-lg uppercase">{t('exportRecipe')}</h3>
                    <button onClick={onClose} className="p-2 text-slate-300 dark:text-slate-600 hover:text-slate-600 dark:hover:text-slate-400 transition-colors shrink-0">
                        <XIcon className="w-6 h-6" />
                    </button>
                </div>

                <div className="p-6 flex-grow overflow-hidden flex flex-col gap-4">
                    <div className="flex-grow overflow-y-auto bg-slate-50 dark:bg-slate-950 rounded-2xl border border-slate-200 dark:border-slate-800 p-4 font-mono text-sm leading-relaxed text-slate-700 dark:text-slate-300 shadow-inner whitespace-pre-wrap select-all">
                        {formattedText}
                    </div>
                </div>

                {/* Footer */}
                <div className="p-6 bg-slate-50 dark:bg-slate-800 border-t dark:border-slate-700 flex gap-3">
                    <button onClick={onClose} className="flex-1 py-4 text-slate-400 dark:text-slate-500 font-bold text-xs uppercase tracking-widest hover:text-slate-600 dark:hover:text-slate-300 transition-colors">{t('close')}</button>
                    <button 
                        onClick={handleCopy}
                        className={`flex-[2] py-4 text-white font-black rounded-xl shadow-xl transition-all text-sm uppercase tracking-widest active:scale-95 flex items-center justify-center gap-2 ${copied ? 'bg-green-600 shadow-green-200 dark:shadow-none' : 'bg-slate-900 dark:bg-slate-700 shadow-slate-200 dark:shadow-slate-900/50 hover:bg-slate-800 dark:hover:bg-slate-600'}`}
                    >
                        {copied ? (
                            <>
                                <span className="text-xl">✓</span> {t('textCopied')}
                            </>
                        ) : (
                            <>
                                <ClipboardIcon className="w-5 h-5" /> {t('copyText')}
                            </>
                        )}
                    </button>
                </div>
            </div>
            <CopiedNoticeModal
                isOpen={showCopiedNotice}
                onClose={() => setShowCopiedNotice(false)}
                messageKey="textCopied"
                language={language}
            />
        </div>
    );
};

export default RecipeExportModal;
